import React, { useState, useEffect, useRef } from 'react';
import { useTranslations } from 'next-intl';
import { useElementScroll } from '@/shared/hooks/dom/scroll/useElementScroll';
import { classNames } from '@/shared/lib/classnames/classnames';
import type { NavbarLink } from '../model/types';
interface Props {
  navbarLinksList: NavbarLink[];
  sendAnalytics: (id: string) => void;
  className?: string;
}
export const Navbar: React.FC<Props> = props => {
  const {
    navbarLinksList,
    className,
    sendAnalytics
  } = props;
  const t = useTranslations();
  const [activeLink, setActiveLink] = useState('');
  const [isSticky, setIsSticky] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const navContentRef = useRef<HTMLElement>(null);
  const {
    scrollToElement: scrollToNavbarLink
  } = useElementScroll(navContentRef);
  const checkOverflow = () => {
    if (navRef.current && navContentRef.current) {
      const containerWidth = navRef.current.clientWidth;
      const contentWidth = navContentRef.current.scrollWidth;
      setHasOverflow(contentWidth > containerWidth);
    }
  };
  const handleLinkClick = (href: string, index: number) => {
    setActiveLink(href);
    sendAnalytics(href);
    scrollToNavbarLink(index);
  };
  const scrollHandler = () => {
    const sections = navbarLinksList.map(item => ({
      section: document.querySelector(item.href),
      link: item.href
    }));
    const found = sections.find(({
      section
    }) => {
      if (section) {
        const rect = section.getBoundingClientRect();
        return rect.top <= 194 && rect.bottom >= 194;
      }
      return false;
    });
    if (found) {
      setActiveLink(found.link);
    }
  };
  const updateHtmlScrollPadding = () => {
    const htmlElement = document.documentElement;
    htmlElement.classList.remove('scroll-pt-20');
    htmlElement.classList.add('scroll-pt-36');
  };
  const scrollToElement = (elem: NavbarLink) => {
    const container = document.querySelector(`#${elem.id}`);
    if (container) {
      container.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  useEffect(() => {
    if (window.location.hash) {
      const found = navbarLinksList.find(item => item.href === window.location.hash);
      if (found) {
        setActiveLink(found.href);
        scrollToElement(found);
        return;
      }
    }
    if (navbarLinksList.length && !activeLink) {
      setActiveLink(navbarLinksList[0].href);
    }
  }, [navbarLinksList]);
  useEffect(() => {
    const navbar = navRef.current;
    const originalTop = navbar?.offsetTop ?? 0;
    const handleScroll = () => {
      if (navbar) {
        const currentScroll = window.scrollY;
        setIsSticky(currentScroll > originalTop);
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  useEffect(() => {
    updateHtmlScrollPadding();
    window.addEventListener('scroll', scrollHandler);
    window.addEventListener('resize', checkOverflow);
    scrollHandler();
    checkOverflow();
    return () => {
      window.removeEventListener('scroll', scrollHandler);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [navbarLinksList]);
  return <section ref={navRef} className={classNames('backdrop-saturate-2 sticky top-20 z-40 flex items-center overflow-x-auto rounded-2xl backdrop-blur-3xl scroll-width-none', {
    'bg-bgPrimary': !isSticky,
    'bg-bgPrimary/10': isSticky,
    'justify-center': !hasOverflow,
    'cursor-grab': hasOverflow
  }, [className])} data-sentry-component="Navbar" data-sentry-source-file="index.tsx">
      <nav ref={navContentRef} className="relative m-4 inline-flex items-center gap-4">
        {navbarLinksList.map((item, index) => <a href={item.href} key={item.id} className={classNames('w-max select-none font-semibold text-textPrimary/50 transition-colors hover:text-textPrimary/70 active:text-textPrimary/80', {
        'text-textPrimary': activeLink === item.href
      })} onClick={() => handleLinkClick(item.href, index)}>
            {typeof item.label === 'string' ? t(item.label) : t(item.label.key, {
          ...item.label.data
        })}
          </a>)}
      </nav>
    </section>;
};